import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtUSD, fmtDate, PARTNER_LABELS } from "@/lib/format";
import { toast } from "sonner";
import { ArrowRight, Scale, Info, HandCoins, Trash2, PiggyBank } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const KPI_HELP = {
    total_capital_deployed: "Total cash both partners have physically paid to suppliers to date (across every parcel — root and sub-parcels combined).",
    total_expenses: "Total out-of-pocket expenses paid by either partner (setting, polishing, shipping, and other item-level costs).",
    total_supplier_debt: "Total amount you still owe suppliers across all parcels (in-stock + already-sold). Only cleared when you record an actual payment to the supplier — the waterfall's supplier gate at sale time is only an earmark.",
    total_supplier_debt_sold: "Debt on parcels that have already been sold but where the supplier still hasn't been paid. Bashari or Ido typically holds this cash from the sale in their pocket until they physically forward it to the supplier.",
    total_supplier_debt_unsold: "Debt on inventory you haven't sold yet.",
    total_revenue: "Sum of every sale's revenue.",
    total_net_profit: "Revenue minus every gate below it: supplier balances cleared, capital returned to partners, and expenses reimbursed. What's left is booked as profit and split 50/50.",
};

const ROW_HELP = {
    capital_contributed: "Total cash each partner has actually paid to suppliers for parcels (sum of all payment records — not the parcel's total cost).",
    capital_recovered: "Portion of paid capital already returned via sales (Gate 2 of the waterfall — after supplier balances are cleared).",
    capital_tied_up: "Capital contributed − capital recovered − pass-through supplier payments. The actual money each partner has out of pocket right now (forwarding a sale's supplier earmark isn't 'funding' — it's just handing the partnership's cash to the supplier).",
    supplier_paid_via_sales: "Of the sale cash each partner physically received, the portion earmarked for the supplier (Gate 0 of the waterfall). This is pass-through, not personal capital.",
    capital_outstanding: "Capital paid − Capital recovered (not pass-through-adjusted). Kept for CSV / audit; the headline fairness number uses 'Capital tied up (my cash)' below.",
    expenses_paid: "Total out-of-pocket expenses each partner has personally covered (setting, polishing, shipping, etc.).",
    expenses_recovered: "Portion of paid expenses already reimbursed via sales.",
    expenses_outstanding: "Expenses paid − Expenses recovered. Reimbursements still due once the associated item sells.",
    profit_earned: "Each partner's share of net profit realized on sold items (50/50 split, after supplier + capital + expenses are covered).",
    total_payouts_received: "Sum of all gates: capital returned + expenses reimbursed + profit share. What each partner is entitled to from every sale so far.",
    net_owed: "Capital outstanding + Expenses outstanding. The amount each partner still needs to recover on unsold inventory (that they've already funded).",
    fair_share: "In a 50/50 partnership, half of the combined 'still tied up' capital + expenses. Each partner's ideal contribution to what's currently in play.",
    funding_gap: "How each partner's actual outstanding contribution compares to the fair share. Positive = over-funded (owed by the partnership); Negative = under-funded (owes the partnership). Shrinks to $0 as inventory sells.",
};

export default function Ledger() {
    const { canWrite } = useAuth();
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settlements, setSettlements] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ amount: "", cash_portion: "", fairness_portion: "", date: new Date().toISOString().slice(0, 10), notes: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [{ data: ld }, { data: sl }] = await Promise.all([
                api.get("/ledger"),
                api.get("/settlements"),
            ]);
            setLedger(ld);
            setSettlements(sl || []);
        } catch { toast.error("Failed to load ledger"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openForm = () => {
        const cs = ledger?.combined_settlement;
        if (!cs?.amount) return;
        setForm({
            amount: String(cs.amount),
            cash_portion: String(cs.cash_portion || 0),
            fairness_portion: String(cs.fairness_portion || 0),
            date: new Date().toISOString().slice(0, 10),
            notes: "",
        });
        setShowForm(true);
    };

    const submitSettlement = async (e) => {
        e.preventDefault();
        const cs = ledger.combined_settlement;
        if (!cs?.debtor || !cs?.creditor) return;
        const amt = Number(form.amount);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
        // Pay down the cash portion first, then the fairness portion (matches invoice-like semantics).
        const cashCap = Number(cs.cash_portion || 0);
        const fairCap = Number(cs.fairness_portion || 0);
        let cashPay = Math.min(amt, cashCap);
        let fairPay = Math.max(0, amt - cashPay);
        // If user overrode portions manually, respect those (and validate sum == amount).
        if (form.cash_portion !== "" && form.fairness_portion !== "") {
            const c = Number(form.cash_portion || 0);
            const f = Number(form.fairness_portion || 0);
            if (Math.abs(c + f - amt) < 0.01) { cashPay = c; fairPay = f; }
        }
        if (fairPay > fairCap + 0.01) { toast.error(`Fairness portion exceeds the open fairness gap ($${fairCap.toFixed(2)})`); return; }
        setSaving(true);
        try {
            await api.post("/settlements", {
                from_partner: cs.debtor,
                to_partner: cs.creditor,
                amount: amt,
                cash_portion: Math.round(cashPay * 100) / 100,
                fairness_portion: Math.round(fairPay * 100) / 100,
                date: form.date,
                notes: form.notes || null,
            });
            toast.success("Settlement recorded");
            setShowForm(false);
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    const deleteSettlement = async (id) => {
        if (!confirm("Delete this settlement payment?")) return;
        try {
            await api.delete(`/settlements/${id}`);
            toast.success("Settlement removed");
            load();
        } catch { toast.error("Failed"); }
    };

    if (loading) return <div className="text-stone-500 text-sm">Loading…</div>;
    if (!ledger) return null;

    const rows = [
        { key: "capital_contributed", label: "Capital contributed" },
        { key: "capital_recovered", label: "− Received back via sales", muted: true },
        { key: "supplier_paid_via_sales", label: "− Pass-through supplier payments", muted: true },
        { key: "capital_tied_up", label: "= Capital tied up (my cash)", emphasise: true },
        { key: "expenses_paid", label: "Expenses paid" },
        { key: "expenses_recovered", label: "Expenses recovered" },
        { key: "expenses_outstanding", label: "Expenses outstanding", emphasise: true },
        { key: "profit_earned", label: "Profit earned" },
        { key: "total_payouts_received", label: "Owed per waterfall" },
        { key: "net_owed", label: "Net owed (unsold inventory)", major: true },
        { key: "fair_share", label: "Fair share of live capital + expenses", divider: true },
        { key: "funding_gap", label: "Funding gap (+ over / − under)" },
    ];

    const cs = ledger.combined_settlement || { amount: 0 };
    const debtorLabel = PARTNER_LABELS[cs.debtor];
    const creditorLabel = PARTNER_LABELS[cs.creditor];
    const hasSettlement = Number(cs.amount || 0) > 0.005;
    const cashPortion = Number(cs.cash_portion || 0);
    const fairPortion = Number(cs.fairness_portion || 0);

    return (
        <TooltipProvider delayDuration={200}>
            <div className="space-y-8 animate-fade-up" data-testid="ledger-page">
                <header>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Real-time settlement view</div>
                    <h1 className="font-display text-4xl sm:text-5xl leading-none tracking-tight">Partner Ledger</h1>
                </header>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <Kpi label="Total capital deployed" value={fmtUSD(ledger.totals.total_capital_deployed)} testid="ledger-total-capital" help={KPI_HELP.total_capital_deployed} />
                    <Kpi label="Total expenses" value={fmtUSD(ledger.totals.total_expenses)} testid="ledger-total-expenses" help={KPI_HELP.total_expenses} />
                    <Kpi label="Owed to suppliers (all)" value={fmtUSD(ledger.totals.total_supplier_debt || 0)} accent={ledger.totals.total_supplier_debt > 0.01 ? "rose" : undefined} testid="ledger-total-supplier-debt" help={KPI_HELP.total_supplier_debt} sub={ledger.totals.total_supplier_paid_via_sales > 0.01 ? `${fmtUSD(ledger.totals.total_supplier_paid_via_sales)} earmarked from sales` : null} />
                    <Kpi label="Owed to suppliers (sold only)" value={fmtUSD(ledger.totals.total_supplier_debt_sold || 0)} accent={ledger.totals.total_supplier_debt_sold > 0.01 ? "rose" : undefined} testid="ledger-total-supplier-debt-sold" help={KPI_HELP.total_supplier_debt_sold} sub={ledger.totals.total_supplier_debt_unsold > 0.01 ? `${fmtUSD(ledger.totals.total_supplier_debt_unsold)} on unsold inventory` : null} />
                    <Kpi label="Total revenue" value={fmtUSD(ledger.totals.total_revenue)} testid="ledger-total-revenue" help={KPI_HELP.total_revenue} />
                    <Kpi label="Total net profit" value={fmtUSD(ledger.totals.total_net_profit)} accent testid="ledger-total-profit" help={KPI_HELP.total_net_profit} />
                </div>

                {/* Combined Partner Settlement (cash + fairness folded into one headline) */}
                <section className="border border-stone-200 bg-[#f5efe0] p-6 space-y-5" data-testid="settlement-panel">
                    <div className="flex items-center gap-2">
                        <Scale className="h-4 w-4 text-stone-600" strokeWidth={1.5} />
                        <h2 className="font-display text-2xl text-stone-900">Settlement due</h2>
                    </div>
                    <p className="text-xs text-stone-600 leading-relaxed max-w-2xl">
                        One number: how much does one partner need to hand the other right now to be square? Includes both cash owed from sales and the implicit IOU for unequal in-stock funding (which auto-repays when the stock sells).
                    </p>

                    {hasSettlement ? (
                        <div className="bg-white border-2 border-amber-500/60 p-5 space-y-3" data-testid="settlement-verdict">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="flex items-center gap-3 flex-wrap text-stone-900">
                                    <div className="text-[10px] uppercase tracking-widest text-amber-700 mr-2">Settlement due</div>
                                    <span className="font-display text-xl">{debtorLabel}</span>
                                    <span className="text-xs uppercase tracking-widest text-stone-500">owes</span>
                                    <span className="font-display text-xl">{creditorLabel}</span>
                                    <ArrowRight className="h-4 w-4 text-stone-500" />
                                    <span className="font-display text-3xl text-amber-700 num" data-testid="settlement-amount">{fmtUSD(cs.amount)}</span>
                                </div>
                                {canWrite && !showForm && (
                                    <Button onClick={openForm} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white gap-2" data-testid="record-settlement-open">
                                        <HandCoins className="h-4 w-4" strokeWidth={1.5} /> Record settlement
                                    </Button>
                                )}
                            </div>
                            {(Math.abs(cashPortion) > 0.005 || Math.abs(fairPortion) > 0.005) && (
                                <div className="text-xs text-stone-600 space-y-1 pt-2 border-t border-stone-200" data-testid="settlement-breakdown">
                                    {Math.abs(cashPortion) > 0.005 && (
                                        <div className="flex justify-between" data-testid="settlement-breakdown-cash">
                                            <span>─ cash from sales (profit payout due)</span>
                                            <span className="num text-stone-800">{cashPortion < 0 ? "−" : ""}{fmtUSD(Math.abs(cashPortion))}</span>
                                        </div>
                                    )}
                                    {Math.abs(fairPortion) > 0.005 && (
                                        <div className="flex justify-between" data-testid="settlement-breakdown-fairness">
                                            <span>─ fairness on in-stock inventory</span>
                                            <span className="num text-stone-800">{fairPortion < 0 ? "−" : ""}{fmtUSD(Math.abs(fairPortion))}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white border border-stone-200 p-5 text-sm text-stone-600" data-testid="settlement-verdict-balanced">
                            Partners are square — nothing owed either way.
                        </div>
                    )}

                    {/* Per-partner mini cards (kept for anyone who wants the detailed view) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {["bashari", "ido"].map(pk => {
                            const p = ledger.partners[pk];
                            const bal = p.cash_balance;
                            const supPaid = p.supplier_paid_via_sales || 0;
                            const advOut = p.fairness_advance_outstanding || 0;
                            const advBack = p.fairness_advance_repaid_to_me || 0;
                            return (
                                <div key={pk} className="bg-white border border-stone-200 p-5" data-testid={`settlement-card-${pk}`}>
                                    <div className="text-[10px] uppercase tracking-widest text-stone-500">{PARTNER_LABELS[pk]}</div>
                                    <div className="mt-3 space-y-1.5 text-sm">
                                        <Row label="Cash received from sales" value={fmtUSD(p.cash_received)} testid={`settlement-${pk}-cash-received`} />
                                        {supPaid > 0.005 && (
                                            <Row label="− Earmarked for supplier (pass-through)" value={fmtUSD(supPaid)} testid={`settlement-${pk}-supplier-paid`} muted />
                                        )}
                                        <Row label="Transferred to partner (cash)" value={fmtUSD(p.settlement_paid || 0)} testid={`settlement-${pk}-transferred-out`} muted />
                                        <Row label="Received from partner (cash)" value={fmtUSD(p.settlement_received || 0)} testid={`settlement-${pk}-transferred-in`} muted />
                                        <Row label="Owed per waterfall" value={fmtUSD(p.total_payouts_received)} testid={`settlement-${pk}-owed`} />
                                        <Row label="Capital tied up (in stock)" value={fmtUSD(p.capital_tied_up_in_stock || 0)} testid={`settlement-${pk}-tied-up-in-stock`} muted />
                                        <Row label="Expenses outstanding (in stock)" value={fmtUSD(p.expenses_outstanding_in_stock || 0)} testid={`settlement-${pk}-expenses-in-stock`} muted />
                                        {advOut > 0.005 && (
                                            <Row label="Open fairness advance (owes back)" value={fmtUSD(advOut)} testid={`settlement-${pk}-advance-out`} muted />
                                        )}
                                        {advBack > 0.005 && (
                                            <Row label="Advance repaid from sales" value={fmtUSD(advBack)} testid={`settlement-${pk}-advance-back`} muted />
                                        )}
                                        <div className="border-t border-stone-200 pt-2 mt-2 flex items-center justify-between">
                                            <div className="text-xs uppercase tracking-widest text-stone-600">Cash balance</div>
                                            <div className={`num text-lg ${bal > 0.005 ? "text-amber-700" : bal < -0.005 ? "text-emerald-700" : "text-stone-500"}`} data-testid={`settlement-${pk}-balance`}>
                                                {bal > 0 ? "+" : ""}{fmtUSD(bal)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {showForm && (
                        <form onSubmit={submitSettlement} className="bg-white border border-emerald-500/40 p-5 space-y-3" data-testid="settlement-form">
                            <div className="text-xs uppercase tracking-widest text-emerald-700">
                                Record: {debtorLabel} → {creditorLabel}
                            </div>
                            {(cashPortion > 0.005 || fairPortion > 0.005) && (
                                <div className="text-[11px] text-stone-500" data-testid="settlement-form-prefill-hint">
                                    Pre-filled with {fmtUSD(cs.amount)} = {fmtUSD(cashPortion)} cash + {fmtUSD(fairPortion)} fairness. Pay less and the cash portion closes first.
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">Amount (USD)</Label>
                                    <Input required type="number" step="0.01" min="0.01" value={form.amount}
                                        onChange={e => setForm({ ...form, amount: e.target.value })}
                                        className="rounded-none bg-[#faf7ef] border-stone-200" data-testid="settlement-amount-input" />
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">Date</Label>
                                    <Input required type="date" value={form.date}
                                        onChange={e => setForm({ ...form, date: e.target.value })}
                                        className="rounded-none bg-[#faf7ef] border-stone-200" data-testid="settlement-date-input" />
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">Notes</Label>
                                    <Textarea value={form.notes} rows={1}
                                        onChange={e => setForm({ ...form, notes: e.target.value })}
                                        className="rounded-none bg-[#faf7ef] border-stone-200 resize-none" data-testid="settlement-notes-input" />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button type="submit" disabled={saving} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="settlement-submit">
                                    {saving ? "Saving…" : "Save settlement"}
                                </Button>
                                <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="rounded-none" data-testid="settlement-cancel">
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    )}

                    {settlements.length > 0 && (
                        <div className="bg-white border border-stone-200" data-testid="settlements-history">
                            <div className="px-5 py-3 border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500">
                                Settlement history
                            </div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-widest text-stone-500 text-left border-b border-stone-100">
                                        <th className="py-2 px-5">Date</th>
                                        <th className="py-2 px-5">Transfer</th>
                                        <th className="py-2 px-5 text-right">Amount</th>
                                        <th className="py-2 px-5">Notes</th>
                                        <th className="py-2 px-5"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {settlements.map(st => (
                                        <tr key={st.id} className="border-b border-stone-100/70 hover:bg-stone-50" data-testid={`settlement-row-${st.id}`}>
                                            <td className="py-2 px-5 text-stone-500">{fmtDate(st.date)}</td>
                                            <td className="py-2 px-5">
                                                <span className="inline-flex items-center gap-2 text-stone-800">
                                                    <span className="font-medium">{PARTNER_LABELS[st.from_partner]}</span>
                                                    <ArrowRight className="h-3.5 w-3.5 text-stone-400" />
                                                    <span className="font-medium">{PARTNER_LABELS[st.to_partner]}</span>
                                                </span>
                                            </td>
                                            <td className="py-2 px-5 text-right num text-stone-900">
                                                {fmtUSD(st.amount)}
                                                {Number(st.fairness_portion || 0) > 0.005 && (
                                                    <div className="text-[10px] text-stone-500 num" data-testid={`settlement-row-${st.id}-breakdown`}>
                                                        {fmtUSD(st.cash_portion || 0)} cash · {fmtUSD(st.fairness_portion || 0)} fairness
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-2 px-5 text-stone-500 text-xs">{st.notes || "—"}</td>
                                            <td className="py-2 px-5 text-right">
                                                {canWrite && (
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteSettlement(st.id)}
                                                        className="text-stone-400 hover:text-rose-600"
                                                        title="Delete this settlement"
                                                        data-testid={`settlement-delete-${st.id}`}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {/* FundingFairnessPanel removed — folded into the Settlement panel above (iter22). */}

                <div className="border border-stone-200 overflow-x-auto" data-testid="ledger-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                                <th className="py-3 px-5">Metric</th>
                                <th className="py-3 px-5 text-right">Bashari</th>
                                <th className="py-3 px-5 text-right">Ido</th>
                                <th className="py-3 px-5 text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const b = ledger.partners.bashari[r.key];
                                const i = ledger.partners.ido[r.key];
                                const help = ROW_HELP[r.key];
                                const gapFmt = (v) => {
                                    if (r.key !== "funding_gap") return fmtUSD(v);
                                    if (Math.abs(v) < 0.005) return fmtUSD(0);
                                    return (v > 0 ? "+" : "−") + fmtUSD(Math.abs(v));
                                };
                                const gapColor = (v) => {
                                    if (r.key !== "funding_gap") return "";
                                    if (Math.abs(v) < 0.005) return "text-stone-500";
                                    return v > 0 ? "text-emerald-700" : "text-rose-600";
                                };
                                return (
                                    <tr key={r.key} className={`border-b border-stone-200/70 ${r.major ? "bg-emerald-50" : r.divider ? "bg-stone-50 border-t-2 border-stone-300" : r.muted ? "bg-stone-50/50" : "hover:bg-stone-100"}`} data-testid={`ledger-row-${r.key}`}>
                                        <td className={`py-3 px-5 ${r.major ? "font-display text-lg text-stone-900" : r.muted ? "text-stone-500 text-xs pl-8" : "text-stone-700"} ${r.emphasise ? "text-stone-900 font-medium" : ""}`}>
                                            <span className="inline-flex items-center gap-1.5">
                                                {r.label}
                                                {help && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button type="button" className="text-stone-400 hover:text-stone-700" aria-label={`Explain ${r.label}`} data-testid={`ledger-tooltip-${r.key}`}>
                                                                <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="right" className="max-w-xs bg-stone-900 text-white text-xs leading-relaxed">
                                                            {help}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </span>
                                        </td>
                                        <td className={`py-3 px-5 text-right num ${r.major ? "text-emerald-700 text-lg" : r.muted ? "text-stone-500 text-xs" : gapColor(b) || "text-stone-800"}`} data-testid={`ledger-${r.key}-bashari`}>{gapFmt(b)}</td>
                                        <td className={`py-3 px-5 text-right num ${r.major ? "text-emerald-700 text-lg" : r.muted ? "text-stone-500 text-xs" : gapColor(i) || "text-stone-800"}`} data-testid={`ledger-${r.key}-ido`}>{gapFmt(i)}</td>
                                        <td className={`py-3 px-5 text-right num text-stone-500 ${r.muted ? "text-xs" : ""}`}>{r.key === "funding_gap" ? "—" : fmtUSD(b + i)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="text-xs text-stone-500 max-w-3xl leading-relaxed space-y-2">
                    <p><b>How to read this ledger</b> — hover any info icon (ⓘ) for a plain-English definition.</p>
                    <p><b>Net owed</b> reflects the amount each partner still needs to recover on unsold inventory they&apos;ve already funded (capital paid + expenses paid, less what&apos;s come back via sales). Once an item sells, its allocated capital and expenses flow through the waterfall to reduce this figure and add to Profit earned.</p>
                    <p><b>Owed to suppliers</b> is separate from partner debt — it&apos;s the amount still due to outside vendors and is not reflected in the partner columns below.</p>
                    <p>The <b>Partner cash settlement</b> panel above tracks who physically collected sale proceeds vs. who they are owed to.</p>
                </div>
            </div>
        </TooltipProvider>
    );
}

const Kpi = ({ label, value, accent, testid, help, sub }) => (
    <div className="border border-stone-200 bg-white p-4">
        <div className="flex items-center gap-1.5">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
            {help && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button type="button" className="text-stone-400 hover:text-stone-700" aria-label={`Explain ${label}`} data-testid={`${testid}-tooltip`}>
                            <Info className="h-3 w-3" strokeWidth={1.5} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs bg-stone-900 text-white text-xs leading-relaxed">
                        {help}
                    </TooltipContent>
                </Tooltip>
            )}
        </div>
        <div className={`text-xl font-light num mt-2 ${accent === "rose" ? "text-rose-600" : accent ? "text-emerald-700" : "text-stone-900"}`} data-testid={testid}>{value}</div>
        {sub && <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-1" data-testid={`${testid}-sub`}>{sub}</div>}
    </div>
);

const Row = ({ label, value, testid, muted }) => (
    <div className="flex items-center justify-between">
        <div className={`text-xs ${muted ? "text-stone-500" : "text-stone-600"}`}>{label}</div>
        <div className={`num ${muted ? "text-stone-500" : "text-stone-900"}`} data-testid={testid}>{value}</div>
    </div>
);

const FundingFairnessPanel = ({ ledger }) => {
    const f = ledger.funding_fairness || {};
    const hasGap = Number(f.amount || 0) > 0.005;
    const b = ledger.partners.bashari;
    const i = ledger.partners.ido;
    return (
        <section className="border border-stone-200 bg-[#f5efe0] p-6 space-y-5" data-testid="funding-fairness-panel">
            <div className="flex items-center gap-2">
                <PiggyBank className="h-4 w-4 text-stone-600" strokeWidth={1.5} />
                <h2 className="font-display text-2xl text-stone-900">Funding equalization</h2>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button type="button" className="text-stone-400 hover:text-stone-700" aria-label="Explain funding equalization" data-testid="funding-fairness-tooltip">
                            <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-md bg-stone-900 text-white text-xs leading-relaxed">
                        In a 50/50 partnership, capital and expenses still tied up in unsold inventory should be split equally. If one partner has fronted more than half, the other has an implicit debt to equalize.
                        This is separate from the cash settlement above and shrinks to $0 as everything sells.
                    </TooltipContent>
                </Tooltip>
            </div>
            <p className="text-xs text-stone-600 leading-relaxed max-w-2xl">
                If you dissolved the partnership right now, how much would need to change hands so both partners are equally invested in the remaining inventory?
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["bashari", "ido"].map(pk => {
                    const p = ledger.partners[pk];
                    const invested = (p.capital_tied_up || 0) + (p.expenses_outstanding || 0);
                    const gap = p.funding_gap || 0;
                    return (
                        <div key={pk} className="bg-white border border-stone-200 p-5" data-testid={`fairness-card-${pk}`}>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">{PARTNER_LABELS[pk]}</div>
                            <div className="mt-3 space-y-1.5 text-sm">
                                <Row label="Capital tied up (my cash)" value={fmtUSD(p.capital_tied_up || 0)} testid={`fairness-${pk}-capital-tied-up`} />
                                <Row label="Expenses outstanding" value={fmtUSD(p.expenses_outstanding || 0)} testid={`fairness-${pk}-expenses-outstanding`} muted />
                                <Row label="Still tied up (capital + expenses)" value={fmtUSD(invested)} testid={`fairness-${pk}-invested`} />
                                <Row label="Fair share (50%)" value={fmtUSD(p.fair_share || 0)} testid={`fairness-${pk}-fair-share`} muted />
                                <div className="border-t border-stone-200 pt-2 mt-2 flex items-center justify-between">
                                    <div className="text-xs uppercase tracking-widest text-stone-600">Gap</div>
                                    <div className={`num text-lg ${Math.abs(gap) < 0.005 ? "text-stone-500" : gap > 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid={`fairness-${pk}-gap`}>
                                        {Math.abs(gap) < 0.005 ? fmtUSD(0) : (gap > 0 ? "+" : "−") + fmtUSD(Math.abs(gap))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {hasGap ? (
                <div className="bg-white border-2 border-sky-500/60 p-5 flex items-center gap-4 flex-wrap" data-testid="fairness-verdict">
                    <div className="text-[10px] uppercase tracking-widest text-sky-700">To equalize the partnership</div>
                    <div className="flex items-center gap-3 flex-wrap text-stone-900">
                        <span className="font-display text-xl">{PARTNER_LABELS[f.debtor]}</span>
                        <span className="text-xs uppercase tracking-widest text-stone-500">owes</span>
                        <span className="font-display text-xl">{PARTNER_LABELS[f.creditor]}</span>
                        <ArrowRight className="h-4 w-4 text-stone-500" />
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button type="button" className="font-display text-2xl text-sky-700 num underline decoration-dotted decoration-sky-300 underline-offset-4" data-testid="fairness-amount">
                                    {fmtUSD(f.amount)}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-md bg-stone-900 text-white text-xs leading-relaxed p-3">
                                <WalkthroughFormula ledger={ledger} />
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </div>
            ) : (
                (Number(b.capital_tied_up || 0) + Number(b.expenses_outstanding || 0) + Number(i.capital_tied_up || 0) + Number(i.expenses_outstanding || 0)) > 0.01 ? (
                    <div className="bg-white border border-stone-200 p-5 text-sm text-stone-600" data-testid="fairness-balanced">
                        Both partners are equally invested in the remaining inventory — no funding equalization needed.
                    </div>
                ) : (
                    <div className="bg-white border border-stone-200 p-5 text-sm text-stone-600" data-testid="fairness-empty">
                        $0 — square. No net personal capital tied up right now.
                    </div>
                )
            )}
        </section>
    );
};

const WalkthroughFormula = ({ ledger }) => {
    const f = ledger.funding_fairness || {};
    const amt = Number(f.amount || 0);
    const line = (pk) => {
        const p = ledger.partners[pk];
        const cap = p.capital_contributed || 0;
        const rec = p.capital_recovered || 0;
        const pass = p.supplier_paid_via_sales || 0;
        const tied = p.capital_tied_up || 0;
        const expOut = p.expenses_outstanding || 0;
        const net = (p.net_invested != null) ? p.net_invested : (tied + expOut);
        return (
            <div className="mt-2">
                <div className="font-semibold">{PARTNER_LABELS[pk]} net invested — {fmtUSD(net)}</div>
                <div className="text-stone-300">
                    {fmtUSD(cap)} contributed − {fmtUSD(rec)} recovered − {fmtUSD(pass)} pass-through = {fmtUSD(tied)}
                    {expOut > 0.005 && <> &nbsp; +&nbsp; {fmtUSD(expOut)} expenses outstanding</>}
                </div>
            </div>
        );
    };
    const b = ledger.partners.bashari;
    const i = ledger.partners.ido;
    const bNet = (b.net_invested != null) ? b.net_invested : ((b.capital_tied_up || 0) + (b.expenses_outstanding || 0));
    const iNet = (i.net_invested != null) ? i.net_invested : ((i.capital_tied_up || 0) + (i.expenses_outstanding || 0));
    const share = b.fair_share || 0;
    return (
        <div>
            <div className="font-display text-sm">To equalize: {fmtUSD(amt)}</div>
            {line("bashari")}
            {line("ido")}
            <div className="border-t border-stone-700 mt-2 pt-2 text-stone-300">
                Combined {fmtUSD(bNet + iNet)} → fair share {fmtUSD(share)} → gap {fmtUSD(amt)}
            </div>
        </div>
    );
};
